import React, { useState, useEffect } from "react";
import {
  Copy,
  ChevronDown,
  Save,
  Layout,
  Maximize,
  Sparkles,
  MessageSquare,
  Code,
  Type,
  Square,
  Plus,
  Brain,
  Zap,
  Palette,
  Sliders,
  Activity,
  Layers,
  Moon,
  Clock,
  Users,
  Trash2,
  X,
  AlertCircle
} from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import CustomDropdown from "../components/CustomDropdown";
import LiveChatSettings from "../components/LiveChatSettings";

const TAB_ORDER = ["design", "features", "parameters", "deployment"];

const hexToRgba = (hex, opacityPercent = 100) => {
  if (!hex) return "#111111";
  const alpha = Math.max(0, Math.min(100, Number(opacityPercent))) / 100;
  let c = hex.replace("#", "").trim();
  if (c.startsWith("rgb") || c.startsWith("hsl")) {
    const parts = c.match(/[\d.]+/g);
    if (parts && parts.length >= 3) {
      if (alpha === 1) {
        return `#${parseInt(parts[0], 10).toString(16).padStart(2, "0")}${parseInt(parts[1], 10).toString(16).padStart(2, "0")}${parseInt(parts[2], 10).toString(16).padStart(2, "0")}`;
      }
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
    }
  }
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  if (c.length === 8) c = c.slice(0, 6);
  const num = parseInt(c, 16);
  if (isNaN(num)) return hex;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  if (alpha === 1) {
    return `#${c.toUpperCase()}`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const extractAlpha = (colorStr) => {
  if (!colorStr) return 100;
  if (colorStr.startsWith("rgba")) {
    const match = colorStr.match(/rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)/);
    if (match) return Math.round(parseFloat(match[1]) * 100);
  }
  if (colorStr.startsWith("#") && colorStr.length === 9) {
    const a = parseInt(colorStr.slice(7, 9), 16);
    return Math.round((a / 255) * 100);
  }
  return 100;
};

const extractBaseHex = (colorStr, fallback = "#111111") => {
  if (!colorStr || typeof colorStr !== "string") return fallback;
  if (colorStr.includes("gradient")) {
    const token = colorStr.match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)/)?.[0];
    if (token) {
      return extractBaseHex(token, fallback);
    }
  }
  if (colorStr.startsWith("rgba") || colorStr.startsWith("rgb")) {
    const parts = colorStr.match(/\d+/g);
    if (parts && parts.length >= 3) {
      const r = parseInt(parts[0], 10).toString(16).padStart(2, "0");
      const g = parseInt(parts[1], 10).toString(16).padStart(2, "0");
      const b = parseInt(parts[2], 10).toString(16).padStart(2, "0");
      return `#${r}${g}${b}`;
    }
  }
  if (colorStr.startsWith("#")) {
    if (colorStr.length === 9) return colorStr.slice(0, 7);
    if (colorStr.length === 7) return colorStr;
    if (colorStr.length === 4) {
      return "#" + colorStr.slice(1).split("").map((x) => x + x).join("");
    }
  }
  return fallback;
};

const parseGradient = (val, fallbackStart = "#9ae0bf", fallbackEnd = "#c38dce") => {
  if (typeof val === "string" && val.includes("gradient")) {
    const angleMatch = val.match(/(\d+)deg/);
    const angle = angleMatch ? angleMatch[1] : "135";
    const colors = val.match(/rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}/g);
    const rawStart = colors?.[0] || fallbackStart;
    const rawEnd = colors?.[1] || fallbackEnd;
    return {
      isGradient: true,
      angle: angle,
      startHex: extractBaseHex(rawStart, fallbackStart),
      startOpacity: extractAlpha(rawStart),
      endHex: extractBaseHex(rawEnd, fallbackEnd),
      endOpacity: extractAlpha(rawEnd),
    };
  }
  const raw = (typeof val === "string" && (val.startsWith("#") || val.startsWith("rgb"))) ? val : fallbackStart;
  return {
    isGradient: false,
    angle: "135",
    startHex: extractBaseHex(raw, fallbackStart),
    startOpacity: extractAlpha(raw),
    endHex: extractBaseHex(fallbackEnd, fallbackEnd),
    endOpacity: 100,
  };
};

const WidgetIntegration = ({ activeAiAgentId, aiAgents, fetchAiAgents }) => {
  const { showToast } = useToast();
  const [widgetConfig, setWidgetConfig] = useState({
    position: "",
    welcomeMessage: "",
    title: "",
    iconUrl: "",
    brandLogoUrl: "",
    headerColor: "#111111",
    headerTitleColor: "#ffffff",
    backgroundColor: "#ffffff",
    userBubbleColor: "#111111",
    botBubbleColor: "#ffffff",
    sendButtonColor: "#111111",
    size: "medium",
    launcherSize: "60px",
    showCloseButton: true,
    showLauncher: true,
    eyecatcherEnabled: true,
    eyecatcherText: "👋 Ask me anything!",
    eyecatcherTheme: "gradient",
    eyecatcherAnimation: "pulse",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    borderRadius: "20px",
    customParameters: [],
    campaignSequentialDisplay: true,
    learningEnabled: true,
    feedbackEnabled: true,
    internetSearchEnabled: false,
    summaryEnabled: true,
    summaryInterval: 5,
    summaryModelId: "gpt-4o-mini",
    mode: "multi",
    streamingEnabled: true,
    responseMode: "ai",
    humanEscalation: true,
    escalationTarget: "internal",
    smartGroupRoutingEnabled: false,
    freshchatGroups: [],
    after_hours_enabled: false,
    after_hours_mode: "business_hours",
    after_hours_timezone: "Asia/Kolkata",
    after_hours_schedule: {
      monday: { enabled: true, start: "09:00", end: "18:00" },
      tuesday: { enabled: true, start: "09:00", end: "18:00" },
      wednesday: { enabled: true, start: "09:00", end: "18:00" },
      thursday: { enabled: true, start: "09:00", end: "18:00" },
      friday: { enabled: true, start: "09:00", end: "18:00" },
      saturday: { enabled: false, start: "09:00", end: "18:00" },
      sunday: { enabled: false, start: "09:00", end: "18:00" }
    },
  });
  const currentAiAgent = aiAgents?.find((p) => p.id === activeAiAgentId);
  const currentAiAgentKey = currentAiAgent?.ai_agent_key || "";
  const [iconPreview, setIconPreview] = useState(null);
  const [brandLogoPreview, setBrandLogoPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activeColorTarget, setActiveColorTarget] = useState("headerColor");
  const [hoveredPreviewElement, setHoveredPreviewElement] = useState(null);
  const [activeTab, setActiveTab] = useState("design");

  // Freshchat Groups Modal State
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroups, setEditingGroups] = useState([]);
  const [groupModalError, setGroupModalError] = useState("");

  const scrollToColorCustomization = (targetKey) => {
    setActiveTab("design");
    setActiveColorTarget(targetKey);
    const el = document.getElementById("color-customization-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-indigo-400", "ring-offset-2");
      setTimeout(() => {
        el.classList.remove("ring-4", "ring-indigo-400", "ring-offset-2");
      }, 1500);
    }
  };

  const SUMMARY_MODEL_OPTIONS = [
    { value: "gpt-4o-mini", label: "GPT-4o Mini (Cost Optimized)" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo (Standard)" },
    { value: "gpt-4o", label: "GPT-4o (High Precision)" },
    { value: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku" },
    { value: "gemini-flash-latest", label: "Gemini 1.5 Flash (Latest)" },
  ];

  const RESERVED_KEYS = [
    "apiUrl", "aiAgentId", "agentKey", "showCloseButton", "showLauncher",
    "title", "welcomeMessage", "position", "size", "primaryColor",
    "secondaryColor", "backgroundColor", "textColor", "borderRadius",
    "width", "height", "fontSize", "fontFamily", "isPreview"
  ];

  const positionOptions = [
    { value: "", label: "Select Position" },
    { value: "bottom-right", label: "Bottom Right" },
    { value: "bottom-left", label: "Bottom Left" },
    { value: "top-right", label: "Top Right" },
    { value: "top-left", label: "Top Left" },
  ];

  const sizeOptions = [
    { value: "small", label: "Small (300×400)" },
    { value: "medium", label: "Medium (350×500)" },
    { value: "large", label: "Large (400×600)" },
  ];



  const fontOptions = [
    { label: 'Inter (Default)', value: 'Inter, sans-serif', style: { fontFamily: 'Inter, sans-serif' } },
    { label: 'Roboto', value: 'Roboto, sans-serif', style: { fontFamily: 'Roboto, sans-serif' } },
    { label: 'Open Sans', value: '"Open Sans", sans-serif', style: { fontFamily: '"Open Sans", sans-serif' } },
    { label: 'Montserrat', value: 'Montserrat, sans-serif', style: { fontFamily: 'Montserrat, sans-serif' } },
    { label: 'Poppins', value: 'Poppins, sans-serif', style: { fontFamily: 'Poppins, sans-serif' } },
    { label: 'Playfair Display', value: '"Playfair Display", serif', style: { fontFamily: '"Playfair Display", serif' } },
    { label: 'Lobster', value: 'Lobster, cursive', style: { fontFamily: 'Lobster, cursive' } },
    { label: 'Courier New', value: '"Courier New", monospace', style: { fontFamily: '"Courier New", monospace' } },
    { label: 'Georgia', value: 'Georgia, serif', style: { fontFamily: 'Georgia, serif' } },
    { label: 'Comic Sans MS', value: '"Comic Sans MS", cursive', style: { fontFamily: '"Comic Sans MS", cursive' } },
    { label: 'System Sans', value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', style: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' } }
  ];
  const eyecatcherThemeOptions = [
    { value: 'gradient', label: 'Gradient' },
    { value: 'glassmorphic', label: 'Glassmorphic' },
    { value: 'classic', label: 'Classic' },
  ];
  const eyecatcherAnimationOptions = [
    { value: 'pulse', label: 'Pulse' },
    { value: 'shake', label: 'Shake' },
    { value: 'float', label: 'Float' },
  ];

  const AFTER_HOURS_MODE_OPTIONS = [
    { value: "business_hours", label: "Business Hours (Support Active Outside)" },
    { value: "after_hours", label: "After-Hours Window (Support Active During)" }
  ];

  const TIMEZONE_OPTIONS = React.useMemo(() => {
    try {
      if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
        const now = new Date();
        return Intl.supportedValuesOf("timeZone").map((tz) => {
          let offset = "";
          try {
            const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(now);
            offset = parts.find((p) => p.type === "timeZoneName")?.value || "";
          } catch (e) {
            // fallback
          }
          const cleanName = tz.replace(/_/g, " ");
          return {
            value: tz,
            label: offset ? `${cleanName} (${offset})` : cleanName
          };
        });
      }
    } catch (e) {
      // fallback
    }
    return [
      { value: "Asia/Kolkata", label: "Asia/Kolkata (GMT+5:30)" },
      { value: "UTC", label: "UTC (GMT)" },
      { value: "America/New_York", label: "America/New York (GMT-4)" },
      { value: "America/Los_Angeles", label: "America/Los Angeles (GMT-7)" },
      { value: "America/Chicago", label: "America/Chicago (GMT-5)" },
      { value: "Europe/London", label: "Europe/London (GMT+1)" },
      { value: "Europe/Paris", label: "Europe/Paris (GMT+2)" },
      { value: "Asia/Dubai", label: "Asia/Dubai (GMT+4)" },
      { value: "Asia/Singapore", label: "Asia/Singapore (GMT+8)" },
      { value: "Asia/Tokyo", label: "Asia/Tokyo (GMT+9)" },
      { value: "Australia/Sydney", label: "Australia/Sydney (GMT+10)" }
    ];
  }, []);

  const DAYS_OF_WEEK = [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" }
  ];

  const [embedCode, setEmbedCode] = useState("");
  const COMMON_PARAMS = [
    { label: "User Full Name", value: "name" },
    { label: "Email Address", value: "email" },
    { label: "Phone Number", value: "phone" },
    { label: "Custom User ID", value: "userid" },
  ];

  const backendUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
  const frontendUrl = backendUrl;

  useEffect(() => {
    if (!aiAgents || aiAgents.length === 0) {
      fetchAiAgents();
    }
  }, [aiAgents, fetchAiAgents]);

  useEffect(() => {
    const loadAllConfig = async () => {
      try {
        if (activeAiAgentId) {
          api.setAiAgentId(activeAiAgentId);
        }
        const [configResponse, campaignsList, logoData] = await Promise.all([
          api.getWidgetConfig(),
          api.getCampaigns().catch(() => []),
          api.getCurrentLogo().catch(() => null)
        ]);

        if (configResponse) {
          setWidgetConfig({
            position: configResponse.position || "",
            welcomeMessage: configResponse.welcome_message || "",
            title: configResponse.title || "",
            iconUrl: configResponse.icon_url || "",
            brandLogoUrl: configResponse.brand_logo_url || "",
            headerColor: configResponse.header_color || "#111111",
            headerTitleColor: configResponse.header_title_color || "#ffffff",
            backgroundColor: configResponse.background_color || "#ffffff",
            userBubbleColor: configResponse.user_bubble_color || "#111111",
            botBubbleColor: configResponse.bot_bubble_color || "#ffffff",
            sendButtonColor: configResponse.send_button_color || "#111111",
            size: configResponse.size || "medium",
            launcherSize: configResponse.launcher_size || "60px",
            showCloseButton: configResponse.show_close_button !== undefined ? configResponse.show_close_button : true,
            showLauncher: configResponse.show_launcher !== undefined ? configResponse.show_launcher : true,
            eyecatcherEnabled: configResponse.eyecatcher_enabled !== undefined ? configResponse.eyecatcher_enabled : true,
            eyecatcherText: configResponse.eyecatcher_text || "👋 Ask me anything!",
            eyecatcherTheme: configResponse.eyecatcher_theme || "gradient",
            eyecatcherAnimation: configResponse.eyecatcher_animation || "pulse",
            fontFamily: configResponse.font_family || "Inter, sans-serif",
            fontSize: configResponse.font_size || "14px",
            borderRadius: configResponse.border_radius || "20px",
            campaignSequentialDisplay: configResponse.campaign_sequential_display !== undefined ? configResponse.campaign_sequential_display : true,
            learningEnabled: configResponse.learning_enabled !== undefined ? configResponse.learning_enabled : true,
            feedbackEnabled: configResponse.feedback_enabled !== undefined ? configResponse.feedback_enabled : true,
            internetSearchEnabled: configResponse.internet_search_enabled !== undefined ? configResponse.internet_search_enabled : false,
            summaryEnabled: configResponse.summary_enabled !== undefined ? configResponse.summary_enabled : true,
            summaryInterval: configResponse.summary_interval || 5,
            summaryModelId: configResponse.summary_model_id || "gpt-4o-mini",
            customParameters: configResponse.custom_parameters || [],
            mode: configResponse.mode || "multi",
            streamingEnabled: configResponse.streaming_enabled !== undefined ? configResponse.streaming_enabled : true,
            responseMode: configResponse.response_mode || "ai",
            humanEscalation: configResponse.human_escalation !== undefined ? configResponse.human_escalation : true,
            escalationTarget: configResponse.escalation_target || "internal",
            smartGroupRoutingEnabled: configResponse.smart_group_routing_enabled !== undefined ? configResponse.smart_group_routing_enabled : false,
            freshchatGroups: Array.isArray(configResponse.freshchat_groups) ? configResponse.freshchat_groups : [],
            after_hours_enabled: configResponse.after_hours_enabled !== undefined ? configResponse.after_hours_enabled : false,
            after_hours_mode: configResponse.after_hours_mode || "business_hours",
            after_hours_timezone: configResponse.after_hours_timezone || "Asia/Kolkata",
            after_hours_schedule: configResponse.after_hours_schedule || {
              monday: { enabled: true, start: "09:00", end: "18:00" },
              tuesday: { enabled: true, start: "09:00", end: "18:00" },
              wednesday: { enabled: true, start: "09:00", end: "18:00" },
              thursday: { enabled: true, start: "09:00", end: "18:00" },
              friday: { enabled: true, start: "09:00", end: "18:00" },
              saturday: { enabled: false, start: "09:00", end: "18:00" },
              sunday: { enabled: false, start: "09:00", end: "18:00" }
            },
          });

          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          if (configResponse.icon_url) {
            setIconPreview(`${baseUrl}${configResponse.icon_url}`);
          }
        }

        if (logoData && logoData.logo_url) {
          setBrandLogoPreview(logoData.logo_url);
        } else if (configResponse?.brand_logo_url) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          const logoUrl = configResponse.brand_logo_url.startsWith("http")
            ? configResponse.brand_logo_url
            : `${baseUrl}${configResponse.brand_logo_url.startsWith("/") ? "" : "/"}${configResponse.brand_logo_url}`;
          setBrandLogoPreview(logoUrl);
        } else if (activeAiAgentId) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          setBrandLogoPreview(`${baseUrl}/api/widget/assets/brand-logo?ai_agent_id=${activeAiAgentId}`);
        }

        if (campaignsList) setCampaigns(campaignsList);
      } catch (error) {
        console.error("Error loading widget data:", error);
      }
    };

    if (activeAiAgentId) {
      loadAllConfig();
    }
  }, [activeAiAgentId]);


  useEffect(() => {
    // Sync current (possibly unsaved) config to sessionStorage for the preview tab
    sessionStorage.setItem('widget_preview_config', JSON.stringify(widgetConfig));
  }, [widgetConfig]);

  // Outside clicks are handled locally by the Dropdown component

  useEffect(() => {
    const generateEmbedCode = () => {
      const userStr = sessionStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const tenantId = currentAiAgent?.tenant_id || user?.tenant_id || "";

      const code = `<script src="${frontendUrl}/widget.js"></script>
<script>
  ChatWidget.init({
    apiUrl: '${backendUrl}',
    aiAgentId: '${activeAiAgentId || ""}',
    tenantId: '${tenantId}',
    agentKey: '${currentAiAgentKey}',
    showCloseButton: ${widgetConfig.showCloseButton},
    showLauncher: ${widgetConfig.showLauncher}${widgetConfig.customParameters.length > 0 ? ',\n    // Custom Parameters:\n    ' + widgetConfig.customParameters.map(p => `${p}: 'VALUE'`).join(',\n    ') : ''}
  });
</script>`;
      setEmbedCode(code);
    };

    if (widgetConfig.title) {
      generateEmbedCode();
    }
  }, [widgetConfig.title, currentAiAgentKey, activeAiAgentId, currentAiAgent, frontendUrl, backendUrl, widgetConfig.showCloseButton, widgetConfig.showLauncher, widgetConfig.customParameters]);

  const saveConfig = async (overrideConfig = null, successMessage = "Configuration saved successfully!") => {
    // Validate launcher size (max 150px)
    if (!overrideConfig && widgetConfig.launcherSize) {
      const parsedSize = parseInt(widgetConfig.launcherSize, 10);
      if (!isNaN(parsedSize) && parsedSize > 150) {
        showToast("Launcher size cannot exceed 150px", "error");
        return;
      }
    } else if (overrideConfig && overrideConfig.launcher_size) {
      const parsedSize = parseInt(overrideConfig.launcher_size, 10);
      if (!isNaN(parsedSize) && parsedSize > 150) {
        showToast("Launcher size cannot exceed 150px", "error");
        return;
      }
    }
    try {
      if (overrideConfig) {
        // Use PATCH for specific field updates
        await api.patchWidgetConfig(overrideConfig);
      } else {
        // Use PATCH for the main form save to avoid overwriting assets
        const partialConfig = {
          position: widgetConfig.position,
          welcome_message: widgetConfig.welcomeMessage,
          title: widgetConfig.title,
          header_color: widgetConfig.headerColor,
          header_title_color: widgetConfig.headerTitleColor,
          background_color: widgetConfig.backgroundColor,
          user_bubble_color: widgetConfig.userBubbleColor,
          bot_bubble_color: widgetConfig.botBubbleColor,
          send_button_color: widgetConfig.sendButtonColor,
          size: widgetConfig.size,
          launcher_size: widgetConfig.launcherSize,
          font_family: widgetConfig.fontFamily,
          font_size: widgetConfig.fontSize,
          border_radius: widgetConfig.borderRadius,
          campaign_sequential_display: widgetConfig.campaignSequentialDisplay,
          learning_enabled: widgetConfig.learningEnabled,
          feedback_enabled: widgetConfig.feedbackEnabled,
          summary_enabled: widgetConfig.summaryEnabled,
          summary_interval: widgetConfig.summaryInterval,
          summary_model_id: widgetConfig.summaryModelId,
          eyecatcher_enabled: widgetConfig.eyecatcherEnabled,
          eyecatcher_text: widgetConfig.eyecatcherText,
          eyecatcher_theme: widgetConfig.eyecatcherTheme,
          eyecatcher_animation: widgetConfig.eyecatcherAnimation,
          mode: widgetConfig.mode,
          streaming_enabled: widgetConfig.streamingEnabled,
          response_mode: widgetConfig.responseMode,
          human_escalation: widgetConfig.humanEscalation,
          escalation_target: widgetConfig.escalationTarget || "internal",
          smart_group_routing_enabled: widgetConfig.smartGroupRoutingEnabled,
          freshchat_groups: widgetConfig.freshchatGroups,
          internet_search_enabled: widgetConfig.internetSearchEnabled,
          after_hours_enabled: widgetConfig.after_hours_enabled,
          after_hours_mode: widgetConfig.after_hours_mode,
          after_hours_timezone: widgetConfig.after_hours_timezone,
          after_hours_schedule: widgetConfig.after_hours_schedule,
        };
        await api.patchWidgetConfig(partialConfig);
      }
      showToast(successMessage, "success");
    } catch (error) {
      console.error("Error saving config:", error);
      showToast("Failed to save configuration", "error");
    }
  };


  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      showToast("Code copied to clipboard!", "success");
    } catch (error) {
      console.error("Error copying embed code:", error);
      showToast("Failed to copy code", "error");
    }
  };



  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        {/* Header matching Knowledge Base / Knowledge Graph / Smart FAQ */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                Widget Interface
              </h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Configure your widget's visual identity, engagement features, context parameters, and deployment script.
            </p>
          </div>

          <div className="self-start sm:self-auto flex flex-col xl:flex-row items-start xl:items-center gap-3">
            {/* Minimal Pill Segmented Tab Switcher */}
            <div className="flex p-1 bg-zinc-100/80 rounded-xl border border-zinc-200/60 w-full sm:w-auto overflow-x-auto custom-scrollbar">
              <button
                type="button"
                onClick={() => setActiveTab("design")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "design"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Palette size={13} />
                <span>Visual Identity</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("features")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "features"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Zap size={13} />
                <span>Features</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("parameters")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "parameters"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Sliders size={13} />
                <span>Parameters</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("deployment")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "deployment"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Code size={13} />
                <span>Deployment</span>
              </button>
            </div>

            {/* Quick Action Button: Save Configuration */}
            <button
              type="button"
              onClick={() => saveConfig(undefined, "Configuration saved successfully!")}
              className="flex items-center gap-2 px-5 py-2 sm:py-2.5 bg-zinc-900 text-white rounded-xl text-xs font-medium hover:bg-zinc-800 transition-all shadow-md active:scale-95 whitespace-nowrap"
            >
              <Save size={13} />
              <span>Save Changes</span>
            </button>
          </div>
        </div>

        {/* Top-Level Sliding Tab Track */}
        <div className="overflow-hidden w-full pt-1">
          <div
            className="flex transition-transform duration-300 ease-out items-start"
            style={{
              transform: `translateX(-${(TAB_ORDER.indexOf(activeTab) * 100) / TAB_ORDER.length}%)`,
              width: `${TAB_ORDER.length * 100}%`,
            }}
          >
            {/* PANEL 1: Visual Identity & Styling */}
            <div className="w-1/4 shrink-0 px-2 sm:px-3">
              <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8 items-start min-h-[calc(100vh-185px)]">
                {/* Left Column: Form & Styling */}
                <div className="space-y-8 animate-slideIn">
                  {/* Card: Dimensions & Typography */}
                  <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden">
                    <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Palette className="text-zinc-700 shrink-0" size={18} />
                        <div>
                          <h3 className="text-sm font-semibold text-zinc-900">
                            Visual Identity
                          </h3>
                          <p className="text-xs text-zinc-400">
                            Configure layout dimensions, typography, and color palette
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 sm:p-8 space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Position */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <Layout size={12} className="text-zinc-400" /> Position
                          </label>
                          <CustomDropdown
                            options={positionOptions}
                            value={widgetConfig.position}
                            onChange={(val) => setWidgetConfig({ ...widgetConfig, position: val })}
                            buttonClassName="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 text-[11px] font-medium flex items-center justify-between"
                          />
                        </div>

                        {/* Initial Size */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <Maximize size={12} className="text-zinc-400" /> Initial Size
                          </label>
                          <CustomDropdown
                            options={sizeOptions}
                            value={widgetConfig.size}
                            onChange={(val) => setWidgetConfig({ ...widgetConfig, size: val })}
                            buttonClassName="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 text-[11px] font-medium flex items-center justify-between"
                          />
                        </div>

                        {/* Launcher Size */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <Maximize size={12} className="text-zinc-400" /> Launcher Size
                          </label>
                          <input
                            type="text"
                            value={widgetConfig.launcherSize}
                            onChange={(e) =>
                              setWidgetConfig({ ...widgetConfig, launcherSize: e.target.value })
                            }
                            placeholder="E.g., 60px or 70"
                            className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-[11px] font-medium transition-all outline-none placeholder:text-zinc-400"
                          />
                        </div>

                        {/* Widget Display Title */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <MessageSquare size={12} className="text-zinc-400" /> Widget Display Title
                          </label>
                          <input
                            type="text"
                            value={widgetConfig.title}
                            onChange={(e) =>
                              setWidgetConfig({ ...widgetConfig, title: e.target.value })
                            }
                            placeholder="E.g., Customer Support"
                            className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-[11px] font-medium transition-all outline-none placeholder:text-zinc-400"
                          />
                        </div>

                        {/* Passive Welcome Hook */}
                        <div className="space-y-1.5 md:col-span-2">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <Sparkles size={12} className="text-zinc-400" /> Passive Welcome Hook
                          </label>
                          <input
                            type="text"
                            value={widgetConfig.welcomeMessage}
                            onChange={(e) =>
                              setWidgetConfig({
                                ...widgetConfig,
                                welcomeMessage: e.target.value,
                              })
                            }
                            placeholder="How can we help you today?"
                            className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-[11px] font-medium transition-all outline-none placeholder:text-zinc-400"
                          />
                        </div>

                        {/* Font Family */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <Type size={12} className="text-zinc-400" /> Font Family
                          </label>
                          <CustomDropdown
                            options={fontOptions}
                            value={widgetConfig.fontFamily}
                            onChange={(val) => setWidgetConfig({ ...widgetConfig, fontFamily: val })}
                            buttonClassName="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 text-[11px] font-medium flex items-center justify-between"
                          />
                        </div>

                        {/* Font Size */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <Type size={12} className="text-zinc-400" /> Font Size
                          </label>
                          <input
                            type="text"
                            value={widgetConfig.fontSize}
                            onChange={(e) => setWidgetConfig({ ...widgetConfig, fontSize: e.target.value })}
                            className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-[11px] font-medium transition-all outline-none placeholder:text-zinc-400"
                            placeholder="14px"
                          />
                        </div>

                        {/* Border Radius */}
                        <div className="space-y-1.5 md:col-span-2">
                          <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                            <Square size={12} className="text-zinc-400" /> Border Radius
                          </label>
                          <input
                            type="text"
                            value={widgetConfig.borderRadius}
                            onChange={(e) => setWidgetConfig({ ...widgetConfig, borderRadius: e.target.value })}
                            className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-[11px] font-medium transition-all outline-none placeholder:text-zinc-400"
                            placeholder="20px"
                          />
                        </div>
                      </div>

                      {/* Color Customization Area */}
                      {(() => {
                        const COLOR_TABS = [
                          { key: "headerColor", label: "Header", defaultStart: "#9ae0bf", defaultEnd: "#c38dce" },
                          { key: "headerTitleColor", label: "Header Title", defaultStart: "#ffffff", defaultEnd: "#f1f5f9" },
                          { key: "backgroundColor", label: "Background", defaultStart: "#ffffff", defaultEnd: "#f8fafc" },
                          { key: "userBubbleColor", label: "User Bubble", defaultStart: "#c38dce", defaultEnd: "#8b5cf6" },
                          { key: "botBubbleColor", label: "Bot Bubble", defaultStart: "#ffffff", defaultEnd: "#f1f5f9" },
                          { key: "sendButtonColor", label: "Action Button", defaultStart: "#55b99b", defaultEnd: "#9ae0bf" },
                        ];

                        const currentTab = COLOR_TABS.find((t) => t.key === activeColorTarget) || COLOR_TABS[0];
                        const currentVal = widgetConfig[currentTab.key] || currentTab.defaultStart;
                        const isLinear = typeof currentVal === "string" && currentVal.includes("gradient");
                        const grad = parseGradient(currentVal, currentTab.defaultStart, currentTab.defaultEnd);

                        const handleModeSwitch = (toLinear) => {
                          if (toLinear) {
                            const s = hexToRgba(grad.startHex, grad.startOpacity);
                            const e = hexToRgba(grad.endHex, grad.endOpacity);
                            setWidgetConfig({
                              ...widgetConfig,
                              [currentTab.key]: `linear-gradient(${grad.angle}deg, ${s} 0%, ${e} 100%)`,
                            });
                          } else {
                            setWidgetConfig({
                              ...widgetConfig,
                              [currentTab.key]: hexToRgba(grad.startHex, grad.startOpacity),
                            });
                          }
                        };

                        const updateGradient = (newStartHex, newEndHex, newAngle, newStartOp, newEndOp) => {
                          const sHex = newStartHex !== undefined ? newStartHex : grad.startHex;
                          const eHex = newEndHex !== undefined ? newEndHex : grad.endHex;
                          const angle = newAngle !== undefined ? newAngle : grad.angle;
                          const sOp = newStartOp !== undefined ? newStartOp : grad.startOpacity;
                          const eOp = newEndOp !== undefined ? newEndOp : grad.endOpacity;

                          const s = hexToRgba(sHex, sOp);
                          const e = hexToRgba(eHex, eOp);

                          setWidgetConfig({
                            ...widgetConfig,
                            [currentTab.key]: `linear-gradient(${angle}deg, ${s} 0%, ${e} 100%)`,
                          });
                        };

                        const updateSolid = (newHex, newOp) => {
                          const hex = newHex !== undefined ? newHex : grad.startHex;
                          const op = newOp !== undefined ? newOp : grad.startOpacity;
                          setWidgetConfig({
                            ...widgetConfig,
                            [currentTab.key]: hexToRgba(hex, op),
                          });
                        };

                        return (
                          <div id="color-customization-section" className="mt-8 p-5 bg-zinc-50/60 rounded-2xl border border-zinc-100 space-y-5 transition-all duration-300 scroll-mt-8">
                            {/* Header: Element Tabs on Left, Solid/Linear Switch on Right */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-zinc-200">
                              {/* Left: Element Selector Tabs */}
                              <div className="flex flex-wrap items-center gap-1.5">
                                {COLOR_TABS.map((tab) => {
                                  const isActive = activeColorTarget === tab.key;
                                  const tabVal = widgetConfig[tab.key] || tab.defaultStart;
                                  return (
                                    <button
                                      key={tab.key}
                                      type="button"
                                      onClick={() => setActiveColorTarget(tab.key)}
                                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                        isActive
                                          ? "bg-white text-zinc-900 shadow-sm border border-zinc-200/80"
                                          : "text-zinc-500 hover:text-zinc-900 hover:bg-white/60"
                                      }`}
                                    >
                                      <span
                                        className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0 shadow-2xs"
                                        style={{ background: tabVal }}
                                      />
                                      {tab.label}
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Right: Solid vs Linear Toggle */}
                              <div className="flex items-center bg-zinc-100/80 p-1 rounded-xl border border-zinc-200/60 shrink-0 self-start sm:self-auto">
                                <button
                                  type="button"
                                  onClick={() => handleModeSwitch(false)}
                                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                                    !isLinear
                                      ? "bg-white text-zinc-900 shadow-sm"
                                      : "text-zinc-500 hover:text-zinc-900"
                                  }`}
                                >
                                  Solid
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleModeSwitch(true)}
                                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                                    isLinear
                                      ? "bg-white text-zinc-900 shadow-sm"
                                      : "text-zinc-500 hover:text-zinc-900"
                                  }`}
                                >
                                  Linear
                                </button>
                              </div>
                            </div>

                            {/* Color Pickers + Opacity + Angle Controls */}
                            <div className="space-y-4">
                              {isLinear ? (
                                <>
                                  {/* Angle Selector Bar */}
                                  <div className="p-3 bg-white rounded-xl border border-zinc-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 flex-1">
                                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider shrink-0">
                                        Angle ({grad.angle}°)
                                      </span>
                                      <input
                                        type="range"
                                        min="0"
                                        max="360"
                                        value={grad.angle}
                                        onChange={(e) => updateGradient(undefined, undefined, e.target.value, undefined, undefined)}
                                        className="w-full max-w-[200px] h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                                      />
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      {["90", "135", "180", "45"].map((deg) => (
                                        <button
                                          key={deg}
                                          type="button"
                                          onClick={() => updateGradient(undefined, undefined, deg, undefined, undefined)}
                                          className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border transition-all ${
                                            grad.angle === deg
                                              ? "bg-zinc-900 text-white border-zinc-900 shadow-sm"
                                              : "bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100"
                                          }`}
                                        >
                                          {deg}°
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Two Color Columns for Gradient */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Start Color */}
                                    <div className="p-4 bg-white rounded-xl border border-zinc-200 space-y-3">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-zinc-700">Start Color</span>
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="color"
                                            value={grad.startHex}
                                            onChange={(e) => updateGradient(e.target.value, undefined, undefined, undefined, undefined)}
                                            className="w-7 h-7 rounded-lg cursor-pointer border border-zinc-200 p-0"
                                          />
                                          <input
                                            type="text"
                                            value={grad.startHex}
                                            onChange={(e) => updateGradient(e.target.value, undefined, undefined, undefined, undefined)}
                                            className="w-20 px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-mono font-bold text-zinc-800"
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-1">
                                        <div className="flex justify-between text-[10px] font-medium text-zinc-500">
                                          <span>Opacity</span>
                                          <span>{grad.startOpacity}%</span>
                                        </div>
                                        <input
                                          type="range"
                                          min="0"
                                          max="100"
                                          value={grad.startOpacity}
                                          onChange={(e) => updateGradient(undefined, undefined, undefined, e.target.value, undefined)}
                                          className="w-full h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                                        />
                                      </div>
                                    </div>

                                    {/* End Color */}
                                    <div className="p-4 bg-white rounded-xl border border-zinc-200 space-y-3">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-zinc-700">End Color</span>
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="color"
                                            value={grad.endHex}
                                            onChange={(e) => updateGradient(undefined, e.target.value, undefined, undefined, undefined)}
                                            className="w-7 h-7 rounded-lg cursor-pointer border border-zinc-200 p-0"
                                          />
                                          <input
                                            type="text"
                                            value={grad.endHex}
                                            onChange={(e) => updateGradient(undefined, e.target.value, undefined, undefined, undefined)}
                                            className="w-20 px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-mono font-bold text-zinc-800"
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-1">
                                        <div className="flex justify-between text-[10px] font-medium text-zinc-500">
                                          <span>Opacity</span>
                                          <span>{grad.endOpacity}%</span>
                                        </div>
                                        <input
                                          type="range"
                                          min="0"
                                          max="100"
                                          value={grad.endOpacity}
                                          onChange={(e) => updateGradient(undefined, undefined, undefined, undefined, e.target.value)}
                                          className="w-full h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                /* Solid Color Mode */
                                <div className="p-4 bg-white rounded-xl border border-zinc-200 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-zinc-700">Color Selection</span>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="color"
                                        value={grad.startHex}
                                        onChange={(e) => updateSolid(e.target.value, undefined)}
                                        className="w-7 h-7 rounded-lg cursor-pointer border border-zinc-200 p-0"
                                      />
                                      <input
                                        type="text"
                                        value={grad.startHex}
                                        onChange={(e) => updateSolid(e.target.value, undefined)}
                                        className="w-20 px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-mono font-bold text-zinc-800"
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex justify-between text-[10px] font-medium text-zinc-500">
                                      <span>Opacity</span>
                                      <span>{grad.startOpacity}%</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={grad.startOpacity}
                                      onChange={(e) => updateSolid(undefined, e.target.value)}
                                      className="w-full h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                </div>

                {/* Right Column: Sticky Live Interactive Preview */}
                <div className="sticky top-6 flex flex-col items-center">
          <div style={{
            width: '340px',
            flexShrink: 0,
            position: 'sticky',
            top: '24px',
            alignSelf: 'flex-start'
          }}>
            {/* Widget Phone Frame */}
            <div style={{
              background: '#ffffff',
              borderRadius: widgetConfig.borderRadius || '24px',
              overflow: 'visible',
              boxShadow: '0 20px 50px -10px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              height: '560px',
              position: 'relative'
            }}>

                {/* Canvas Background with Grid Lines */}
                {(() => {
                  const previewPrimaryColor = extractBaseHex(widgetConfig.headerColor, '#111111');
                  const customBg = widgetConfig.backgroundColor || '#ffffff';
                  return (
                    <div 
                      onClick={() => scrollToColorCustomization('backgroundColor')}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: widgetConfig.borderRadius || '24px',
                        overflow: 'hidden',
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        zIndex: 0,
                        background: customBg,
                        transition: 'all 0.28s ease',
                        boxShadow: hoveredPreviewElement === 'backgroundColor' ? 'inset 0 0 0 3px #6366f1' : 'none'
                      }}
                    >
                      {/* Subtle Tech Grid Overlay */}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `linear-gradient(to right, color-mix(in srgb, ${previewPrimaryColor} 22%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in srgb, ${previewPrimaryColor} 22%, transparent) 1px, transparent 1px)`,
                        backgroundSize: '38px 38px',
                        opacity: 0.35,
                        maskImage: 'radial-gradient(ellipse 70% 65% at 50% 50%, #000 40%, transparent 100%)',
                        WebkitMaskImage: 'radial-gradient(ellipse 70% 65% at 50% 50%, #000 40%, transparent 100%)',
                        pointerEvents: 'none'
                      }} />
                    </div>
                  );
                })()}

                {/* Widget Header Area with stable hover boundary */}
                <div
                  onMouseEnter={() => setHoveredPreviewElement('header')}
                  onMouseLeave={() => setHoveredPreviewElement(null)}
                  style={{
                    position: 'relative',
                    zIndex: 20,
                    flexShrink: 0
                  }}
                >
                  <div
                    onClick={() => scrollToColorCustomization('headerColor')}
                    style={{
                      background: widgetConfig.headerColor || '#111111',
                      padding: '16px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      borderRadius: `${widgetConfig.borderRadius || '24px'} ${widgetConfig.borderRadius || '24px'} 0 0`,
                      transition: 'transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)',
                      transform: hoveredPreviewElement === 'header' ? 'translateY(-14px)' : 'translateY(0)',
                      boxShadow: hoveredPreviewElement === 'header'
                        ? '0 22px 42px -4px rgba(0,0,0,0.38), 0 8px 18px rgba(0,0,0,0.18)'
                        : '0 0 0 0 rgba(0,0,0,0)',
                      willChange: 'transform, box-shadow',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden'
                    }}
                  >
                    {/* Bot Avatar / Brand Logo */}
                    <div style={{
                      maxHeight: '36px',
                      maxWidth: '42px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {(brandLogoPreview || iconPreview) ? (
                        <img
                          src={brandLogoPreview || iconPreview}
                          alt="brand logo"
                          style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                          onError={(e) => {
                            if (iconPreview && e.currentTarget.src !== iconPreview) {
                              e.currentTarget.src = iconPreview;
                            } else {
                              setBrandLogoPreview(null);
                              setIconPreview(null);
                            }
                          }}
                        />
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {(() => {
                        const titleColor = widgetConfig.headerTitleColor || '#ffffff';
                        const isGrad = typeof titleColor === 'string' && titleColor.includes('gradient');
                        return (
                          <div
                            onMouseEnter={() => setHoveredPreviewElement('headerTitle')}
                            onMouseLeave={() => setHoveredPreviewElement(null)}
                            onClick={(e) => {
                              e.stopPropagation();
                              scrollToColorCustomization('headerTitleColor');
                            }}
                            style={{
                              fontWeight: 700,
                              fontSize: '14px',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              display: 'inline-block',
                              maxWidth: '100%',
                              transition: 'all 0.24s cubic-bezier(0.34, 1.56, 0.64, 1)',
                              transform: hoveredPreviewElement === 'headerTitle' ? 'translateY(-2px) scale(1.04)' : 'none',
                              ...(isGrad ? {
                                background: titleColor,
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                color: 'transparent',
                              } : {
                                color: titleColor
                              }),
                              filter: hoveredPreviewElement === 'headerTitle' ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.35))' : 'none'
                            }}
                          >
                            {widgetConfig.title || 'Chat Assistant'}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      background: 'rgba(255,255,255,0.18)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Messages Area (transparent so canvas background shows through) */}
                <div 
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      scrollToColorCustomization('backgroundColor');
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '18px 16px 82px 16px',
                    background: 'transparent',
                    overflowY: 'auto',
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px'
                  }}>
                  {/* Welcome Message */}
                  {widgetConfig.welcomeMessage && (
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <div
                        onMouseEnter={() => setHoveredPreviewElement('bot_welcome')}
                        onMouseLeave={() => setHoveredPreviewElement(null)}
                        onClick={() => scrollToColorCustomization('botBubbleColor')}
                        style={{
                          background: widgetConfig.botBubbleColor || 'rgba(255, 255, 255, 0.88)',
                          backdropFilter: 'blur(16px)',
                          WebkitBackdropFilter: 'blur(16px)',
                          borderRadius: '4px 20px 20px 20px',
                          border: '1px solid rgba(148, 163, 184, 0.2)',
                          padding: '12px 16px',
                          fontSize: '13px',
                          color: '#0f172a',
                          maxWidth: '82%',
                          fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                          lineHeight: 1.5,
                          cursor: 'pointer',
                          position: 'relative',
                          zIndex: hoveredPreviewElement === 'bot_welcome' ? 15 : 1,
                          transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          transform: hoveredPreviewElement === 'bot_welcome' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                          boxShadow: hoveredPreviewElement === 'bot_welcome'
                            ? '0 18px 36px -4px rgba(0,0,0,0.22), 0 6px 14px rgba(0,0,0,0.08)'
                            : '0 4px 15px rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        {widgetConfig.welcomeMessage}
                      </div>
                    </div>
                  )}

                  {/* Sample bot message */}
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div
                      onMouseEnter={() => setHoveredPreviewElement('bot_sample')}
                      onMouseLeave={() => setHoveredPreviewElement(null)}
                      onClick={() => scrollToColorCustomization('botBubbleColor')}
                      style={{
                        background: widgetConfig.botBubbleColor || 'rgba(255, 255, 255, 0.88)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        borderRadius: '4px 20px 20px 20px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        padding: '12px 16px',
                        fontSize: '13px',
                        color: '#0f172a',
                        maxWidth: '82%',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: hoveredPreviewElement === 'bot_sample' ? 15 : 1,
                        transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: hoveredPreviewElement === 'bot_sample' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                        boxShadow: hoveredPreviewElement === 'bot_sample'
                          ? '0 18px 36px -4px rgba(0,0,0,0.22), 0 6px 14px rgba(0,0,0,0.08)'
                          : '0 4px 15px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      👋 Hi! How can I help you today?
                    </div>
                  </div>

                  {/* Sample user message */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexDirection: 'row-reverse' }}>
                    <div
                      onMouseEnter={() => setHoveredPreviewElement('user')}
                      onMouseLeave={() => setHoveredPreviewElement(null)}
                      onClick={() => scrollToColorCustomization('userBubbleColor')}
                      style={{
                        background: widgetConfig.userBubbleColor || '#111111',
                        borderRadius: '20px 20px 4px 20px',
                        padding: '12px 16px',
                        fontSize: '13px',
                        color: '#ffffff',
                        maxWidth: '82%',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: hoveredPreviewElement === 'user' ? 15 : 1,
                        transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: hoveredPreviewElement === 'user' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                        boxShadow: hoveredPreviewElement === 'user'
                          ? '0 18px 36px -4px rgba(0,0,0,0.28), 0 6px 14px rgba(0,0,0,0.12)'
                          : '0 4px 15px rgba(0, 0, 0, 0.08)',
                      }}
                    >
                      I have a question about my order.
                    </div>
                  </div>

                  {/* Bot response */}
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div
                      onMouseEnter={() => setHoveredPreviewElement('bot_response')}
                      onMouseLeave={() => setHoveredPreviewElement(null)}
                      onClick={() => scrollToColorCustomization('botBubbleColor')}
                      style={{
                        background: widgetConfig.botBubbleColor || 'rgba(255, 255, 255, 0.88)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        borderRadius: '4px 20px 20px 20px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        padding: '12px 16px',
                        fontSize: '13px',
                        color: '#0f172a',
                        maxWidth: '82%',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: hoveredPreviewElement === 'bot_response' ? 15 : 1,
                        transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: hoveredPreviewElement === 'bot_response' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                        boxShadow: hoveredPreviewElement === 'bot_response'
                          ? '0 18px 36px -4px rgba(0,0,0,0.22), 0 6px 14px rgba(0,0,0,0.08)'
                          : '0 4px 15px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      Sure! Please share your order ID and I'll look it up right away.
                    </div>
                  </div>
                </div>

                {/* Floating Input Island Dock (matching original widget) */}
                <div style={{
                  position: 'absolute',
                  bottom: '14px',
                  left: '14px',
                  right: '14px',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  pointerEvents: 'none'
                }}>
                  {/* Input Pill Wrapper */}
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    background: '#ffffff',
                    borderRadius: '26px',
                    boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.10), 0 2px 6px 0 rgba(15, 23, 42, 0.04)',
                    padding: '0 12px 0 16px',
                    pointerEvents: 'auto'
                  }}>
                    <input
                      readOnly
                      placeholder="Type a message..."
                      style={{
                        flex: 1,
                        padding: '12px 6px 12px 0',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: '13px',
                        color: '#475569',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif'
                      }}
                    />
                    <button
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94a3b8'
                      }}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                      </svg>
                    </button>
                  </div>

                  {/* Send Button (3D Hover & Click) */}
                  <div
                    onMouseEnter={() => setHoveredPreviewElement('send')}
                    onMouseLeave={() => setHoveredPreviewElement(null)}
                    onClick={() => scrollToColorCustomization('sendButtonColor')}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: widgetConfig.sendButtonColor || '#111111',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: hoveredPreviewElement === 'send' ? 20 : 1,
                      pointerEvents: 'auto',
                      transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      transform: hoveredPreviewElement === 'send' ? 'translateY(-8px) scale(1.15)' : 'translateY(0) scale(1)',
                      boxShadow: hoveredPreviewElement === 'send'
                        ? '0 20px 36px -2px rgba(0,0,0,0.42), 0 8px 16px rgba(0,0,0,0.2)'
                        : '0 8px 22px -2px rgba(15, 23, 42, 0.20), inset 0 1px 1.5px rgba(255, 255, 255, 0.4)',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" style={{ transform: 'rotate(45deg) translate(-1px, 1px)' }}>
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </div>
                </div>
              </div>

           

          </div>
                 
                </div>
              </div>
            </div>

            {/* PANEL 2: Features & Behaviors */}
            <div className="w-1/4 shrink-0 px-2 sm:px-3">
              <div className="space-y-8 min-h-[calc(100vh-185px)] animate-slideIn">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  {/* Left: Agent Capabilities */}
                  <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden">
                    <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Zap className="text-zinc-700 shrink-0" size={18} />
                        <div>
                          <h3 className="text-sm font-semibold text-zinc-900">
                            Agent Capabilities
                          </h3>
                          <p className="text-xs text-zinc-400">
                            Configure execution mode and interactive protocols
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 sm:p-8 space-y-6">
                      {/* Response Mode Selector */}
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Chatbot Response Type</h4>
                          <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider mt-0.5">Dynamic AI Agent reasoning or structured visual Botflows</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div
                            onClick={async () => {
                              setWidgetConfig({ ...widgetConfig, responseMode: "ai" });
                              try {
                                await api.patchWidgetConfig({ response_mode: "ai" });
                                showToast("AI Agent Mode Enabled", "success");
                              } catch (error) {
                                console.error("Error setting response mode:", error);
                                showToast("Failed to update response mode", "error");
                              }
                            }}
                            className={`p-3.5 rounded-xl cursor-pointer border transition-all ${
                              widgetConfig.responseMode === "ai"
                                ? "bg-zinc-900 text-white border-zinc-900 shadow-md"
                                : "bg-zinc-50/50 border-zinc-100 text-zinc-700 hover:bg-zinc-100 hover:border-zinc-200"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Brain size={15} className={widgetConfig.responseMode === "ai" ? "text-white" : "text-zinc-400"} />
                              <span className="text-xs font-bold">AI Agent</span>
                            </div>
                            <p className={`text-[10px] leading-relaxed ${widgetConfig.responseMode === "ai" ? "text-zinc-300" : "text-zinc-400"}`}>
                              Dynamic reasoning based on FAQs, instructions, and tools.
                            </p>
                          </div>

                          <div
                            onClick={async () => {
                              setWidgetConfig({ ...widgetConfig, responseMode: "botflow" });
                              try {
                                await api.patchWidgetConfig({ response_mode: "botflow" });
                                showToast("Visual Botflow Mode Enabled", "success");
                              } catch (error) {
                                console.error("Error setting response mode:", error);
                                showToast("Failed to update response mode", "error");
                              }
                            }}
                            className={`p-3.5 rounded-xl cursor-pointer border transition-all ${
                              widgetConfig.responseMode === "botflow"
                                ? "bg-zinc-900 text-white border-zinc-900 shadow-md"
                                : "bg-zinc-50/50 border-zinc-100 text-zinc-700 hover:bg-zinc-100 hover:border-zinc-200"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Code size={15} className={widgetConfig.responseMode === "botflow" ? "text-white" : "text-zinc-400"} />
                              <span className="text-xs font-bold">Visual Botflow</span>
                            </div>
                            <p className={`text-[10px] leading-relaxed ${widgetConfig.responseMode === "botflow" ? "text-zinc-300" : "text-zinc-400"}`}>
                              Structured flowchart routes (quick replies, inputs, delay paths).
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* AI Execution Mode (when AI response mode enabled) */}
                      {widgetConfig.responseMode === "ai" && (
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">AI Execution Mode</h4>
                            <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider mt-0.5">Choose how queries are distributed and answered</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div
                              onClick={async () => {
                                setWidgetConfig({ ...widgetConfig, mode: "single" });
                                try {
                                  await api.patchWidgetConfig({ mode: "single" });
                                  showToast("Solo Agent Mode Enabled", "success");
                                } catch (error) {
                                  console.error("Error setting agent mode:", error);
                                  showToast("Failed to update agent mode", "error");
                                }
                              }}
                              className={`p-3.5 rounded-xl cursor-pointer border transition-all ${
                                widgetConfig.mode === "single"
                                  ? "bg-zinc-900 text-white border-zinc-900 shadow-md"
                                  : "bg-zinc-50/50 border-zinc-100 text-zinc-700 hover:bg-zinc-100 hover:border-zinc-200"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Zap size={15} className={widgetConfig.mode === "single" ? "text-white" : "text-zinc-400"} />
                                <span className="text-xs font-bold">Solo Agent</span>
                              </div>
                              <p className={`text-[10px] leading-relaxed ${widgetConfig.mode === "single" ? "text-zinc-300" : "text-zinc-400"}`}>
                                One agent handles conversation for lowest latency.
                              </p>
                            </div>

                            <div
                              onClick={async () => {
                                setWidgetConfig({ ...widgetConfig, mode: "multi" });
                                try {
                                  await api.patchWidgetConfig({ mode: "multi" });
                                  showToast("Agent Team Mode Enabled", "success");
                                } catch (error) {
                                  console.error("Error setting agent mode:", error);
                                  showToast("Failed to update agent mode", "error");
                                }
                              }}
                              className={`p-3.5 rounded-xl cursor-pointer border transition-all ${
                                widgetConfig.mode === "multi"
                                  ? "bg-zinc-900 text-white border-zinc-900 shadow-md"
                                  : "bg-zinc-50/50 border-zinc-100 text-zinc-700 hover:bg-zinc-100 hover:border-zinc-200"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Layers size={15} className={widgetConfig.mode === "multi" ? "text-white" : "text-zinc-400"} />
                                <span className="text-xs font-bold">Agent Team</span>
                              </div>
                              <p className={`text-[10px] leading-relaxed ${widgetConfig.mode === "multi" ? "text-zinc-300" : "text-zinc-400"}`}>
                                Team of specialized agents collaborate on requests.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Interactive Feature Switches */}
                      <div className="space-y-3">
                        {/* Streaming */}
                        <div className="flex items-center justify-between p-4 bg-zinc-50/50 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all">
                          <div className="space-y-0.5 pr-4">
                            <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Streaming Responses</h4>
                            <p className="text-[10px] text-zinc-400 font-medium">Stream answers in real-time token by token.</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              checked={widgetConfig.streamingEnabled}
                              onChange={async () => {
                                const newValue = !widgetConfig.streamingEnabled;
                                setWidgetConfig({ ...widgetConfig, streamingEnabled: newValue });
                                try {
                                  await api.patchWidgetConfig({ streaming_enabled: newValue });
                                  showToast(newValue ? "Streaming Enabled" : "Streaming Disabled", "success");
                                } catch (error) {
                                  console.error("Error updating streaming setting:", error);
                                  showToast("Failed to update streaming setting", "error");
                                }
                              }}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                          </label>
                        </div>

                        {/* Feedback Rating */}
                        <div className="flex items-center justify-between p-4 bg-zinc-50/50 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all">
                          <div className="space-y-0.5 pr-4">
                            <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Message Feedback</h4>
                            <p className="text-[10px] text-zinc-400 font-medium">Show thumbs up/down rating on AI messages.</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              checked={widgetConfig.feedbackEnabled}
                              onChange={async () => {
                                const newValue = !widgetConfig.feedbackEnabled;
                                setWidgetConfig({ ...widgetConfig, feedbackEnabled: newValue });
                                try {
                                  await api.patchWidgetConfig({ feedback_enabled: newValue });
                                  showToast(newValue ? "Feedback enabled" : "Feedback disabled", "success");
                                } catch (error) {
                                  console.error("Error updating feedback setting:", error);
                                  showToast("Failed to update feedback setting", "error");
                                }
                              }}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                          </label>
                        </div>

                        {/* Human Escalation */}
                        <div className="p-4 bg-zinc-50/50 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5 pr-4">
                              <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Human Escalation</h4>
                              <p className="text-[10px] text-zinc-400 font-medium">Allow assistant hand off to human agents upon user request.</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer shrink-0">
                              <input
                                type="checkbox"
                                checked={widgetConfig.humanEscalation}
                                onChange={async () => {
                                  const newValue = !widgetConfig.humanEscalation;
                                  setWidgetConfig({ ...widgetConfig, humanEscalation: newValue });
                                  try {
                                    await api.patchWidgetConfig({ human_escalation: newValue });
                                    showToast(newValue ? "Human Escalation Enabled" : "Human Escalation Disabled", "success");
                                  } catch (error) {
                                    console.error("Error updating human escalation setting:", error);
                                    showToast("Failed to update human escalation setting", "error");
                                  }
                                }}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                            </label>
                          </div>

                          {/* Escalation Target Dropdown */}
                          {widgetConfig.humanEscalation && (
                            <div className="pt-2.5 border-t border-zinc-200/60 flex items-center justify-between gap-4">
                              <div className="space-y-0.5">
                                <label className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wide">Escalation Target</label>
                                <p className="text-[10px] text-zinc-400">Route requests to internal live agents or Freshchat inbox</p>
                              </div>
                              <div className="w-56 shrink-0">
                                <CustomDropdown
                                  options={[
                                    { value: "internal", label: "Internal Agents (Live Chat)" },
                                    { value: "freshchat", label: "Freshchat (Inbox)" },
                                  ]}
                                  value={widgetConfig.escalationTarget || "internal"}
                                  onChange={async (newTarget) => {
                                    setWidgetConfig({ ...widgetConfig, escalationTarget: newTarget });
                                    try {
                                      await api.patchWidgetConfig({ escalation_target: newTarget });
                                      showToast(`Escalation Target set to ${newTarget === 'freshchat' ? 'Freshchat' : 'Internal Agents'}`, "success");
                                    } catch (err) {
                                      console.error("Error updating escalation target:", err);
                                      showToast("Failed to update escalation target", "error");
                                    }
                                  }}
                                  buttonClassName="!py-1.5 !px-3 !rounded-xl !text-xs font-semibold"
                                />
                              </div>
                            </div>
                          )}

                          {/* Smart Group Routing (Visible only when Human Escalation = ON and Escalation Target = Freshchat) */}
                          {widgetConfig.humanEscalation && widgetConfig.escalationTarget === "freshchat" && (
                            <div className="pt-3 border-t border-zinc-200/60 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="space-y-0.5 pr-4">
                                  <div className="flex items-center gap-2">
                                    <h5 className="text-[11px] font-bold text-zinc-900 uppercase tracking-wider">Smart Group Routing</h5>
                                    {widgetConfig.freshchatGroups?.length > 0 ? (
                                      <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md">
                                        Custom Groups: {widgetConfig.freshchatGroups.length}
                                      </span>
                                    ) : widgetConfig.smartGroupRoutingEnabled ? (
                                      <div className="relative group inline-flex items-center">
                                        <span className="cursor-help px-2 py-0.5 text-[9px] font-semibold bg-zinc-100 text-zinc-800 border border-zinc-300 rounded-md flex items-center gap-1 shadow-sm hover:bg-zinc-200 transition-colors">
                                          <Sparkles size={9} className="text-zinc-600" />
                                          Auto Fetch Active
                                        </span>
                                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:flex flex-col w-64 p-2 bg-zinc-900 text-white text-[10px] rounded-lg shadow-xl border border-zinc-800 z-50 pointer-events-none transition-all">
                                          <span className="font-semibold text-zinc-100 mb-0.5 flex items-center gap-1">
                                            <Sparkles size={11} className="text-zinc-400" />
                                            Automatic Group Assignment
                                          </span>
                                          <span className="text-zinc-300 leading-snug">
                                            No custom groups added. When an escalation occurs, groups will be listed directly from Freshchat, and AI will automatically evaluate and assign the most suitable group.
                                          </span>
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                  <p className="text-[10px] text-zinc-500 font-medium">Route conversations to the most relevant Freshchat group using AI.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={widgetConfig.smartGroupRoutingEnabled}
                                    onChange={async () => {
                                      const newValue = !widgetConfig.smartGroupRoutingEnabled;
                                      setWidgetConfig({ ...widgetConfig, smartGroupRoutingEnabled: newValue });
                                      try {
                                        await api.patchWidgetConfig({ smart_group_routing_enabled: newValue });
                                        showToast(newValue ? "Smart Group Routing Enabled" : "Smart Group Routing Disabled", "success");
                                      } catch (error) {
                                        console.error("Error updating smart group routing:", error);
                                        showToast("Failed to update smart group routing setting", "error");
                                      }
                                    }}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                                </label>
                              </div>

                              <div className="flex items-center justify-between pt-1">
                                {widgetConfig.freshchatGroups?.length > 0 ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingGroups(JSON.parse(JSON.stringify(widgetConfig.freshchatGroups || [])));
                                        setGroupModalError("");
                                        setIsGroupModalOpen(true);
                                      }}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 bg-white border border-zinc-300 rounded-lg shadow-sm hover:bg-zinc-50 hover:border-zinc-400 transition-all"
                                    >
                                      <Users size={13} className="text-zinc-500" />
                                      Manage Custom Groups
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingGroups([
                                        { group_id: "", group_name: "", description: "" }
                                      ]);
                                      setGroupModalError("");
                                      setIsGroupModalOpen(true);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-900 bg-zinc-100 hover:bg-zinc-200 border border-zinc-300 rounded-lg transition-all"
                                  >
                                    <Plus size={13} />
                                    Add Custom Groups
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Memory Summarization */}
                        <div className="p-4 bg-zinc-50/50 rounded-xl border border-zinc-100 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5 pr-4">
                              <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Memory Summarization</h4>
                              <p className="text-[10px] text-zinc-400 font-medium">Compress long threads into rolling context summaries.</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer shrink-0">
                              <input
                                type="checkbox"
                                checked={widgetConfig.summaryEnabled}
                                onChange={async () => {
                                  const newValue = !widgetConfig.summaryEnabled;
                                  setWidgetConfig({ ...widgetConfig, summaryEnabled: newValue });
                                  try {
                                    await api.patchWidgetConfig({ summary_enabled: newValue });
                                    showToast(newValue ? "Summarization Enabled" : "Summarization Disabled", "success");
                                  } catch (error) {
                                    console.error("Error updating summary setting:", error);
                                    showToast("Failed to update summary setting", "error");
                                  }
                                }}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                            </label>
                          </div>

                          {widgetConfig.summaryEnabled && (
                            <div className="pt-2 space-y-3 border-t border-zinc-100">
                              <div className="space-y-1.5">
                                <label className="text-xs font-medium text-zinc-600">Summarization Model</label>
                                <CustomDropdown
                                  options={SUMMARY_MODEL_OPTIONS}
                                  value={widgetConfig.summaryModelId}
                                  onChange={(val) => {
                                    const newConfig = { ...widgetConfig, summaryModelId: val };
                                    setWidgetConfig(newConfig);
                                    const opt = SUMMARY_MODEL_OPTIONS.find(o => o.value === val);
                                    saveConfig({ summary_model_id: val }, `Summarization model set to ${opt ? opt.label : val}`);
                                  }}
                                  buttonClassName="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-900 flex items-center justify-between"
                                  className="w-full"
                                />
                              </div>

                              <div className="space-y-2">
                                <div className="flex justify-between items-center text-xs">
                                  <span className="font-medium text-zinc-600">Summarization Interval</span>
                                  <span className="font-bold text-zinc-900">Every {widgetConfig.summaryInterval} messages</span>
                                </div>
                                <input
                                  type="range"
                                  min="1"
                                  max="20"
                                  step="1"
                                  value={widgetConfig.summaryInterval}
                                  onChange={(e) => setWidgetConfig({ ...widgetConfig, summaryInterval: parseInt(e.target.value) })}
                                  onMouseUp={async () => {
                                    try {
                                      await api.patchWidgetConfig({ summary_interval: widgetConfig.summaryInterval });
                                      showToast(`Interval set to ${widgetConfig.summaryInterval} messages`, "success");
                                    } catch (error) {
                                      console.error("Error updating summary interval:", error);
                                      showToast("Failed to update interval", "error");
                                    }
                                  }}
                                  className="w-full h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-zinc-900"
                                />
                                <div className="flex justify-between text-[10px] text-zinc-400 font-medium">
                                  <span>1 (Frequent)</span>
                                  <span>20 (Lazy)</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Eye Catcher Greeting */}
                  <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden">
                    <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Sparkles className="text-zinc-700 shrink-0" size={18} />
                        <div>
                          <h3 className="text-sm font-semibold text-zinc-900">
                            Eye Catcher Greeting
                          </h3>
                          <p className="text-xs text-zinc-400">
                            Floating prompt bubble above launcher
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 sm:p-8 space-y-6">
                      {/* Toggle Eye Catcher */}
                      <div className="flex items-center justify-between p-4 bg-zinc-50/50 rounded-xl border border-zinc-100">
                        <div className="space-y-0.5 pr-4">
                          <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Greeting Bubble</h4>
                          <p className="text-[10px] text-zinc-400 font-medium">Floating prompt bubble to attract visitor attention.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={widgetConfig.eyecatcherEnabled}
                            onChange={async () => {
                              const newValue = !widgetConfig.eyecatcherEnabled;
                              setWidgetConfig({ ...widgetConfig, eyecatcherEnabled: newValue });
                              try {
                                await api.patchWidgetConfig({ eyecatcher_enabled: newValue });
                                showToast(newValue ? "Eye Catcher enabled" : "Eye Catcher disabled", "success");
                              } catch (error) {
                                console.error("Error updating Eye Catcher setting:", error);
                                showToast("Failed to update Eye Catcher", "error");
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                        </label>
                      </div>

                      {/* Text Input */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
                          Eye Catcher Prompt Text
                        </label>
                        <input
                          type="text"
                          value={widgetConfig.eyecatcherText}
                          onChange={(e) => setWidgetConfig({ ...widgetConfig, eyecatcherText: e.target.value })}
                          onBlur={async (e) => {
                            try {
                              await api.patchWidgetConfig({ eyecatcher_text: e.target.value });
                              showToast("Eye Catcher text saved!", "success");
                            } catch (error) {
                              console.error("Error saving Eye Catcher text:", error);
                              showToast("Failed to save Eye Catcher text", "error");
                            }
                          }}
                          placeholder="Hey there! Have questions? Click here."
                          className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-[11px] font-medium transition-all outline-none placeholder:text-zinc-400"
                        />
                      </div>

                      {/* Theme & Animation */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600">Theme</label>
                          <CustomDropdown
                            options={eyecatcherThemeOptions}
                            value={widgetConfig.eyecatcherTheme}
                            onChange={async (val) => {
                              setWidgetConfig({ ...widgetConfig, eyecatcherTheme: val });
                              try {
                                await api.patchWidgetConfig({ eyecatcher_theme: val });
                                showToast('Eye Catcher theme updated!', 'success');
                              } catch (error) {
                                console.error('Error updating Eye Catcher theme:', error);
                                showToast('Failed to update theme', 'error');
                              }
                            }}
                            buttonClassName="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 text-[11px] font-medium flex items-center justify-between"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-600">Animation</label>
                          <CustomDropdown
                            options={eyecatcherAnimationOptions}
                            value={widgetConfig.eyecatcherAnimation}
                            onChange={async (val) => {
                              setWidgetConfig({ ...widgetConfig, eyecatcherAnimation: val });
                              try {
                                await api.patchWidgetConfig({ eyecatcher_animation: val });
                                showToast('Eye Catcher animation updated!', 'success');
                              } catch (error) {
                                console.error('Error updating Eye Catcher animation:', error);
                                showToast('Failed to update animation', 'error');
                              }
                            }}
                            buttonClassName="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 text-[11px] font-medium flex items-center justify-between"
                          />
                        </div>
                      </div>

                      {/* Show Launcher Toggle */}
                      <div className="flex items-center justify-between p-4 bg-zinc-50/50 rounded-xl border border-zinc-100">
                        <div className="space-y-0.5 pr-4">
                          <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Launcher Visibility</h4>
                          <p className="text-[10px] text-zinc-400 font-medium">Show floating bubble trigger at the bottom edge.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={widgetConfig.showLauncher}
                            onChange={async () => {
                              const newValue = !widgetConfig.showLauncher;
                              setWidgetConfig({ ...widgetConfig, showLauncher: newValue });
                              try {
                                await api.patchWidgetConfig({ show_launcher: newValue });
                                showToast("Launcher visibility updated!", "success");
                              } catch (error) {
                                console.error("Error updating launcher:", error);
                                showToast("Failed to update launcher", "error");
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live chat: routing to agents, timeouts, End chat (saved separately on the server) */}
                <LiveChatSettings />

                {/* AI After-Hours Support Card */}
                <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden w-full transition-all">
                  <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-zinc-900 text-white flex items-center justify-center shadow-xs">
                        <Moon size={18} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-zinc-900">
                            AI After-Hours Support
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${
                            widgetConfig.after_hours_enabled
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                              : "bg-zinc-100 text-zinc-500"
                          }`}>
                            {widgetConfig.after_hours_enabled ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-400">
                          Provide 24/7 coverage with custom AI instructions outside business hours
                        </p>
                      </div>
                    </div>

                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={widgetConfig.after_hours_enabled}
                        onChange={async () => {
                          const newValue = !widgetConfig.after_hours_enabled;
                          setWidgetConfig({ ...widgetConfig, after_hours_enabled: newValue });
                          try {
                            await api.patchWidgetConfig({ after_hours_enabled: newValue });
                            showToast(newValue ? "After-Hours Support enabled!" : "After-Hours Support disabled!", "success");
                          } catch (error) {
                            console.error("Error updating after hours support:", error);
                            showToast("Failed to update after-hours support", "error");
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
                    </label>
                  </div>

                  {widgetConfig.after_hours_enabled && (
                    <div className="p-6 sm:p-7 space-y-6 animate-fadeIn">
                      {/* Schedule Mode & Timezone Pickers */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider flex items-center gap-1.5">
                            <Clock size={12} className="text-zinc-500" />
                            Schedule Type
                          </label>
                          <CustomDropdown
                            options={AFTER_HOURS_MODE_OPTIONS}
                            value={widgetConfig.after_hours_mode}
                            onChange={async (val) => {
                              setWidgetConfig({ ...widgetConfig, after_hours_mode: val });
                              try {
                                await api.patchWidgetConfig({ after_hours_mode: val });
                                showToast("Schedule type updated!", "success");
                              } catch (err) {
                                console.error("Error updating schedule mode:", err);
                                showToast("Failed to update schedule type", "error");
                              }
                            }}
                            buttonClassName="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-xs font-medium flex items-center justify-between"
                          />
                          <p className="text-[10px] text-zinc-400">
                            {widgetConfig.after_hours_mode === "business_hours"
                              ? "AI after-hours instructions activate outside the configured hours below."
                              : "AI after-hours instructions activate during the configured hours below."}
                          </p>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider flex items-center gap-1.5">
                            Timezone
                          </label>
                          <CustomDropdown
                            options={TIMEZONE_OPTIONS}
                            value={widgetConfig.after_hours_timezone}
                            onChange={async (val) => {
                              setWidgetConfig({ ...widgetConfig, after_hours_timezone: val });
                              try {
                                await api.patchWidgetConfig({ after_hours_timezone: val });
                                showToast("Timezone updated!", "success");
                              } catch (err) {
                                console.error("Error updating timezone:", err);
                                showToast("Failed to update timezone", "error");
                              }
                            }}
                            buttonClassName="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-xs font-medium flex items-center justify-between"
                          />
                          <p className="text-[10px] text-zinc-400">
                            Current time evaluated against this timezone for your visitors.
                          </p>
                        </div>
                      </div>

                      {/* Weekly Schedule Table */}
                      <div className="space-y-3 pt-2 border-t border-zinc-100">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                              Weekly Schedule
                            </h4>
                            <p className="text-[11px] text-zinc-400">
                              Define opening and closing times for each day of the week.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              const monSchedule = widgetConfig.after_hours_schedule?.monday || { enabled: true, start: "09:00", end: "18:00" };
                              const cleanMon = {
                                ...monSchedule,
                                ranges: monSchedule.enabled && monSchedule.start && monSchedule.end
                                  ? [{ start: monSchedule.start, end: monSchedule.end }]
                                  : (monSchedule.ranges || [])
                              };
                              const updatedSchedule = {
                                ...widgetConfig.after_hours_schedule,
                                monday: { ...cleanMon },
                                tuesday: { ...cleanMon },
                                wednesday: { ...cleanMon },
                                thursday: { ...cleanMon },
                                friday: { ...cleanMon }
                              };
                              setWidgetConfig({ ...widgetConfig, after_hours_schedule: updatedSchedule });
                              try {
                                await api.patchWidgetConfig({ after_hours_schedule: updatedSchedule });
                                showToast("Monday hours copied to Mon–Fri!", "success");
                              } catch (err) {
                                console.error("Error copying hours:", err);
                                showToast("Failed to copy schedule", "error");
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors cursor-pointer"
                          >
                            <Copy size={11} />
                            Copy Monday to all weekdays
                          </button>
                        </div>

                        <div className="bg-zinc-50/50 rounded-xl border border-zinc-100 divide-y divide-zinc-100 overflow-hidden">
                          {DAYS_OF_WEEK.map(({ key, label }) => {
                            const dayConfig = widgetConfig.after_hours_schedule?.[key] || {
                              enabled: key !== "saturday" && key !== "sunday",
                              start: "09:00",
                              end: "18:00"
                            };

                            const updateDay = async (updatedDayConfig) => {
                              const s = updatedDayConfig.start || "09:00";
                              const e = updatedDayConfig.end || "18:00";
                              const cleanedConfig = {
                                ...updatedDayConfig,
                                start: s,
                                end: e,
                                ranges: updatedDayConfig.enabled ? [{ start: s, end: e }] : []
                              };
                              const newSchedule = {
                                ...(widgetConfig.after_hours_schedule || {}),
                                [key]: cleanedConfig
                              };
                              setWidgetConfig({ ...widgetConfig, after_hours_schedule: newSchedule });
                              try {
                                await api.patchWidgetConfig({ after_hours_schedule: newSchedule });
                              } catch (err) {
                                console.error(`Error saving schedule for ${key}:`, err);
                                showToast("Failed to save schedule change", "error");
                              }
                            };

                            return (
                              <div
                                key={key}
                                className="p-3 sm:px-4 flex items-center justify-between flex-wrap gap-3 hover:bg-white transition-colors"
                              >
                                <div className="w-28 flex items-center gap-2">
                                  <span className="text-xs font-semibold text-zinc-900">{label}</span>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap">
                                  {dayConfig.enabled ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="time"
                                        value={dayConfig.start || "09:00"}
                                        onChange={(e) => updateDay({ ...dayConfig, start: e.target.value })}
                                        className="px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-mono text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                                      />
                                      <span className="text-xs text-zinc-400 font-medium">to</span>
                                      <input
                                        type="time"
                                        value={dayConfig.end || "18:00"}
                                        onChange={(e) => updateDay({ ...dayConfig, end: e.target.value })}
                                        className="px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-mono text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                                      />
                                    </div>
                                  ) : (
                                    <span className="text-xs font-medium text-zinc-400 italic px-2">
                                      Closed all day (Full After-Hours)
                                    </span>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() => updateDay({ ...dayConfig, enabled: !dayConfig.enabled })}
                                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors cursor-pointer ${
                                      dayConfig.enabled
                                        ? "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                                        : "bg-amber-50 text-amber-700 border border-amber-200/60"
                                    }`}
                                  >
                                    {dayConfig.enabled ? "Set Closed" : "Set Open"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Live Engagement Protocols Card */}
                <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden w-full">
                  <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Activity className="text-zinc-700 shrink-0" size={18} />
                      <div>
                        <h3 className="text-sm font-semibold text-zinc-900">
                          Live Engagement Protocols
                        </h3>
                        <p className="text-xs text-zinc-400">
                          Currently active campaigns and rules on your widget
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {campaigns.filter(c => c.is_active).length} Active
                    </div>
                  </div>

                  <div className="p-6 sm:p-8 space-y-3">
                    {campaigns.filter(c => c.is_active).length === 0 ? (
                      <div className="py-8 text-center bg-zinc-50/50 rounded-2xl border border-dashed border-zinc-200">
                        <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">No active protocols detected</p>
                        <p className="text-[9px] text-zinc-400 mt-1">Configure proactive engagement campaigns in Campaign Management.</p>
                      </div>
                    ) : (
                      campaigns.filter(c => c.is_active).map(campaign => (
                        <div key={campaign.id} className="flex items-center justify-between p-3.5 bg-zinc-50/50 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all group">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm border border-zinc-100">
                              {campaign.data?.image ? (
                                campaign.data.image.startsWith('data:') || campaign.data.image.startsWith('http') || campaign.data.image.includes('/') || campaign.data.image.includes('.webp') || campaign.data.image.includes('static') ? (
                                  <img
                                    src={campaign.data.image}
                                    alt={campaign.data?.name || 'Campaign'}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-base">{campaign.data.image}</span>
                                )
                              ) : (
                                <Sparkles size={16} className="text-zinc-400" />
                              )}
                            </div>
                            <div>
                              <h4 className="text-xs font-bold text-zinc-900">{campaign.data?.name || 'Untitled Campaign'}</h4>
                              <p className="text-[10px] text-zinc-400 font-medium">Priority: P{campaign.priority} • Type: {campaign.data?.type || 'Standard'}</p>
                            </div>
                          </div>
                          <div className="px-2.5 py-1 bg-white border border-zinc-200 rounded-lg text-[9px] font-bold text-zinc-500 uppercase tracking-wider">
                            Live Now
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* PANEL 3: Context Parameters */}
            <div className="w-1/4 shrink-0 px-2 sm:px-3">
              <div className="min-h-[calc(100vh-185px)] animate-slideIn space-y-8">
                <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden">
                  <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Sliders className="text-zinc-700 shrink-0" size={18} />
                      <div>
                        <h3 className="text-sm font-semibold text-zinc-900">
                          Context Parameters
                        </h3>
                        <p className="text-xs text-zinc-400">
                          Pass custom attributes from your website directly to agent memory
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 sm:p-8 space-y-6">
                    <div className="flex flex-col md:flex-row gap-3 items-stretch">
                      {/* Common Parameters Dropdown */}
                      <CustomDropdown
                        options={COMMON_PARAMS}
                        value=""
                        onChange={(val) => {
                          const opt = COMMON_PARAMS.find(o => o.value === val);
                          const isRegistered = widgetConfig.customParameters.includes(val);
                          if (isRegistered) {
                            showToast(`"${val}" is already registered`, "warning");
                          } else {
                            const newParams = [...widgetConfig.customParameters, val];
                            setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                            saveConfig({ custom_parameters: newParams }, `${opt ? opt.label : val} added!`);
                          }
                        }}
                        buttonClassName="w-full md:w-56 px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-900 hover:bg-zinc-100 transition-all flex items-center justify-between"
                        renderSelected={() => (
                          <span className="flex items-center gap-2">
                            <Plus size={14} /> Presets
                          </span>
                        )}
                        renderOption={(opt) => {
                          const isRegistered = widgetConfig.customParameters.includes(opt.value);
                          return (
                            <div className={`flex justify-between items-center w-full ${isRegistered ? "text-zinc-300 cursor-not-allowed" : ""}`}>
                              <span>{opt.label}</span>
                              <span className="text-[10px] font-mono opacity-50">{opt.value}</span>
                            </div>
                          );
                        }}
                      />

                      <div className="hidden md:flex items-center text-zinc-300 px-1 font-bold text-[10px] uppercase">Or</div>

                      {/* Manual Input */}
                      <div className="flex gap-2 flex-1">
                        <input
                          id="new-param-input"
                          type="text"
                          placeholder="e.g. order_id, user_tier"
                          className="flex-1 px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-1 focus:ring-zinc-900 text-zinc-900 text-xs font-medium placeholder:text-zinc-400 outline-none transition-all"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const val = e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
                              if (!val) return;
                              if (RESERVED_KEYS.some(k => k.toLowerCase() === val)) {
                                showToast(`"${val}" is reserved.`, "error");
                                return;
                              }
                              if (!widgetConfig.customParameters.includes(val)) {
                                const newParams = [...widgetConfig.customParameters, val];
                                setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                                saveConfig({ custom_parameters: newParams }, "Custom parameter added!");
                                e.target.value = '';
                              } else {
                                showToast(`"${val}" exists.`, "warning");
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.getElementById('new-param-input');
                            const val = input.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
                            if (!val) return;
                            if (RESERVED_KEYS.some(k => k.toLowerCase() === val)) {
                              showToast(`"${val}" is reserved.`, "error");
                              return;
                            }
                            if (!widgetConfig.customParameters.includes(val)) {
                              const newParams = [...widgetConfig.customParameters, val];
                              setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                              saveConfig({ custom_parameters: newParams }, "Custom parameter added!");
                              input.value = '';
                            }
                          }}
                          className="px-5 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-black transition-all font-bold text-xs uppercase tracking-wider shadow-sm flex items-center gap-1.5 active:scale-95 whitespace-nowrap"
                        >
                          <Plus size={13} />
                          <span>Add</span>
                        </button>
                      </div>
                    </div>

                    {/* Chips */}
                    <div className="flex flex-wrap gap-2.5 pt-2">
                      {widgetConfig.customParameters.length === 0 ? (
                        <div className="w-full py-8 text-center bg-zinc-50/50 rounded-2xl border border-dashed border-zinc-200">
                          <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider leading-relaxed">
                            No custom parameters registered yet.<br />Parameters allow your host application to inject context directly into AI memory.
                          </p>
                        </div>
                      ) : (
                        widgetConfig.customParameters.map((param) => (
                          <div
                            key={param}
                            className="group flex items-center gap-2 px-3.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-mono text-zinc-900 hover:border-zinc-300 shadow-2xs transition-all"
                          >
                            <span>{param}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const newParams = widgetConfig.customParameters.filter(p => p !== param);
                                setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                                saveConfig({ custom_parameters: newParams }, "Parameter removed!");
                              }}
                              className="text-zinc-400 hover:text-red-500 transition-colors"
                            >
                              <ChevronDown size={14} className="rotate-45" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>

                    <p className="text-[9px] text-zinc-400 font-medium">
                      * Only lowercase alphanumeric and underscores allowed. Registered parameters will be pre-populated in your deployment script.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* PANEL 4: Deployment Script */}
            <div className="w-1/4 shrink-0 px-2 sm:px-3">
              <div className="min-h-[calc(100vh-185px)] animate-slideIn space-y-8">
                {/* Embed Script Card */}
                <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden w-full">
                  <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Code className="text-zinc-700 shrink-0" size={18} />
                      <div>
                        <h3 className="text-sm font-semibold text-zinc-900">
                          Deployment Script
                        </h3>
                        <p className="text-xs text-zinc-400">
                          Inject into your website before closing &lt;/body&gt; tag
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-xl hover:bg-black transition-all font-bold text-xs uppercase tracking-wider shadow-sm active:scale-95"
                    >
                      <Copy size={13} />
                      <span>Copy Script</span>
                    </button>
                  </div>

                  <div className="p-6 sm:p-8">
                    <div className="relative group">
                      <pre className="bg-zinc-900 p-6 rounded-2xl text-xs border border-zinc-800 font-mono text-emerald-400/90 leading-relaxed shadow-inner w-full whitespace-pre-wrap break-all overflow-x-auto">
                        <code>{embedCode || "// Generating unique script..."}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Configure Freshchat Groups Centered Modal */}
      {isGroupModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-950/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl border border-zinc-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-zinc-900 text-white">
                    <Users size={16} />
                  </div>
                  <h3 className="text-base font-bold text-zinc-900">Configure Freshchat Groups</h3>
                </div>
                <p className="text-xs text-zinc-500">
                  Add groups that the AI can use when routing human escalation conversations.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsGroupModalOpen(false);
                  setGroupModalError("");
                }}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Error Banner */}
            {groupModalError && (
              <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-xs text-red-700">
                <AlertCircle size={15} className="shrink-0 text-red-600" />
                <span>{groupModalError}</span>
              </div>
            )}

            {/* Modal Body - Groups List */}
            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              {editingGroups.length === 0 ? (
                <div className="text-center py-8 px-4 border-2 border-dashed border-zinc-200 rounded-2xl space-y-3">
                  <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto text-zinc-400">
                    <Users size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-700">No Freshchat Groups Added</p>
                    <p className="text-xs text-zinc-400 mt-0.5">Add your first group to enable intelligent AI-based routing.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingGroups([{ group_id: "", group_name: "", description: "" }]);
                      setGroupModalError("");
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-zinc-900 hover:bg-black rounded-lg transition-all"
                  >
                    <Plus size={14} />
                    Add First Group
                  </button>
                </div>
              ) : (
                editingGroups.map((group, index) => (
                  <div
                    key={index}
                    className="p-4 bg-zinc-50/70 border border-zinc-200/80 rounded-xl space-y-3 relative group transition-all hover:border-zinc-300"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-zinc-900 uppercase tracking-wider">
                        Group {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = editingGroups.filter((_, idx) => idx !== index);
                          setEditingGroups(updated);
                        }}
                        className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete Group"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-zinc-700 mb-1">
                          Group ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. 123456"
                          value={group.group_id || ""}
                          onChange={(e) => {
                            const updated = [...editingGroups];
                            updated[index] = { ...updated[index], group_id: e.target.value.trim() };
                            setEditingGroups(updated);
                          }}
                          className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-zinc-700 mb-1">
                          Group Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Billing Support"
                          value={group.group_name || ""}
                          onChange={(e) => {
                            const updated = [...editingGroups];
                            updated[index] = { ...updated[index], group_name: e.target.value };
                            setEditingGroups(updated);
                          }}
                          className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[11px] font-semibold text-zinc-700">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <span className="text-[10px] text-zinc-400 font-medium">Used by AI as routing criteria</span>
                      </div>
                      <textarea
                        rows={2}
                        placeholder="Handles billing issues, payment failures, invoices, refunds and subscription questions."
                        value={group.description || ""}
                        onChange={(e) => {
                          const updated = [...editingGroups];
                          updated[index] = { ...updated[index], description: e.target.value };
                          setEditingGroups(updated);
                        }}
                        className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900 resize-none leading-relaxed"
                      />
                    </div>
                  </div>
                ))
              )}

              {/* Add Another Group Button */}
              {editingGroups.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingGroups([
                      ...editingGroups,
                      { group_id: "", group_name: "", description: "" }
                    ]);
                  }}
                  className="w-full py-2.5 px-4 border border-dashed border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50/50 rounded-xl text-xs font-semibold text-zinc-700 flex items-center justify-center gap-1.5 transition-all"
                >
                  <Plus size={14} />
                  + Add Another Group
                </button>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3.5 bg-zinc-50 border-t border-zinc-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setIsGroupModalOpen(false);
                  setGroupModalError("");
                }}
                className="px-4 py-2 text-xs font-semibold text-zinc-600 hover:text-zinc-900 bg-white border border-zinc-200 rounded-xl hover:bg-zinc-100 transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  // Validation
                  setGroupModalError("");
                  const seenIds = new Set();
                  for (let i = 0; i < editingGroups.length; i++) {
                    const g = editingGroups[i];
                    const gid = (g.group_id || "").trim();
                    const gname = (g.group_name || "").trim();
                    const gdesc = (g.description || "").trim();

                    if (!gid) {
                      setGroupModalError(`Group #${i + 1} is missing a Group ID.`);
                      return;
                    }
                    if (!gname) {
                      setGroupModalError(`Group #${i + 1} is missing a Group Name.`);
                      return;
                    }
                    if (!gdesc) {
                      setGroupModalError(`Group #${i + 1} is missing a Description. Description is required for AI routing.`);
                      return;
                    }
                    if (seenIds.has(gid)) {
                      setGroupModalError(`Duplicate Group ID "${gid}" detected. Group IDs must be unique.`);
                      return;
                    }
                    seenIds.add(gid);
                  }

                  const cleanedGroups = editingGroups.map((g) => ({
                    group_id: (g.group_id || "").trim(),
                    group_name: (g.group_name || "").trim(),
                    description: (g.description || "").trim(),
                  }));

                  // Preserve existing smart group routing setting even if custom groups are cleared
                  const updatedSmartRouting = widgetConfig.smartGroupRoutingEnabled;

                  const newConfig = {
                    ...widgetConfig,
                    freshchatGroups: cleanedGroups,
                    smartGroupRoutingEnabled: updatedSmartRouting,
                  };

                  setWidgetConfig(newConfig);

                  try {
                    await api.patchWidgetConfig({
                      freshchat_groups: cleanedGroups,
                      smart_group_routing_enabled: updatedSmartRouting,
                    });
                    showToast("Freshchat groups saved successfully!", "success");
                    setIsGroupModalOpen(false);
                  } catch (err) {
                    console.error("Failed to save Freshchat groups:", err);
                    setGroupModalError("Failed to save groups to server. Please try again.");
                  }
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-zinc-900 hover:bg-black rounded-xl transition-all shadow-sm active:scale-95"
              >
                Save Groups
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetIntegration;
